import React, { useState, useEffect, useMemo } from 'react';
import {
    LayoutDashboard,
    Package,
    ShoppingCart,
    FileText,
    TrendingUp,
    AlertCircle,
    Plus,
    Search,
    Trash2,
    Save,
    X,
    UploadCloud,
    CheckCircle,
    Menu,
    ChevronRight,
    DollarSign,
    BarChart3,
    Edit,
    ArrowUp,
    ArrowDown,
    Percent
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line
} from 'recharts';
import { Modal } from './components/Modal';


// --- Tipos y Modelos ---
type Product = {
    id: string;
    name: string;
    sku: string;
    category: string;
    stock: number;
    measure: string;
    price: number;
    taxRate: number;
    discount: number;
    lastPrice: number;
    updatedAt: string;
    currentPrice?: number;
    currentStock?: number;
};

type SaleItem = {
    productId: string;
    quantity: number;
    priceAtSale: number;
};

type SaleOrder = {
    id: string;
    date: string;
    items: SaleItem[];
    total: number;
    status: 'completed' | 'pending';
};

// --- Datos Iniciales (Mock Actualizados) ---
const INITIAL_INVENTORY: Product[] = [
    { id: '1', name: 'Laptop Gamer X1', sku: 'TEC-001', category: 'Tecnología', stock: 15, measure: 'Unidad', price: 1200, taxRate: 19, discount: 50, lastPrice: 1150, updatedAt: '2023-10-01' },
    { id: '2', name: 'Monitor 24" IPS', sku: 'TEC-002', category: 'Tecnología', stock: 8, measure: 'Unidad', price: 180, taxRate: 19, discount: 0, lastPrice: 180, updatedAt: '2023-10-05' },
    { id: '3', name: 'Silla Ergonómica', sku: 'OF-101', category: 'Oficina', stock: 4, measure: 'Unidad', price: 250, taxRate: 19, discount: 10, lastPrice: 280, updatedAt: '2023-10-10' },
    { id: '4', name: 'Teclado Mecánico', sku: 'TEC-003', category: 'Tecnología', stock: 30, measure: 'Unidad', price: 85, taxRate: 19, discount: 5, lastPrice: 80, updatedAt: '2023-10-12' },
    { id: '5', name: 'Escritorio Elevable', sku: 'OF-102', category: 'Oficina', stock: 0, measure: 'Unidad', price: 400, taxRate: 19, discount: 0, lastPrice: 400, updatedAt: '2023-10-15' },
];

const INITIAL_SALES: SaleOrder[] = [
    { id: 'ORD-001', date: '2023-10-20', total: 1380, status: 'completed', items: [{ productId: '1', quantity: 1, priceAtSale: 1200 }, { productId: '2', quantity: 1, priceAtSale: 180 }] },
];

// --- Componentes ---

export default function App() {
    const [activeTab, setActiveTab] = useState<'dashboard' | 'inventory' | 'sales' | 'import'>('dashboard');
    const [inventory, setInventory] = useState<Product[]>(INITIAL_INVENTORY);
    const [sales, setSales] = useState<SaleOrder[]>(INITIAL_SALES);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    // Modal States
    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
    }>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { }
    });

    const [alertModal, setAlertModal] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        type: 'success' | 'error' | 'warning' | 'info';
    }>({
        isOpen: false,
        title: '',
        message: '',
        type: 'info'
    });

    // Persistencia simple
    useEffect(() => {
        const savedInv = localStorage.getItem('inventory_pro_data');
        if (savedInv) {
            try {
                const parsed = JSON.parse(savedInv);
                const migrated = parsed.map((p: any) => ({
                    ...p,
                    measure: p.measure || 'Unidad',
                    taxRate: p.taxRate ?? 0,
                    discount: p.discount ?? 0
                }));
                setInventory(migrated);
            } catch (e) { console.error("Error cargando datos", e); }
        }
        const savedSales = localStorage.getItem('inventory_pro_sales');
        if (savedSales) {
            try {
                setSales(JSON.parse(savedSales));
            } catch (e) { console.error("Error cargando ventas", e); }
        }
    }, []);

    useEffect(() => {
        localStorage.setItem('inventory_pro_data', JSON.stringify(inventory));
    }, [inventory]);

    useEffect(() => {
        localStorage.setItem('inventory_pro_sales', JSON.stringify(sales));
    }, [sales]);

    const renderContent = () => {
        switch (activeTab) {
            case 'dashboard': return <DashboardView inventory={inventory} sales={sales} />;
            case 'inventory': return <InventoryView inventory={inventory} setInventory={setInventory} setConfirmModal={setConfirmModal} setAlertModal={setAlertModal} />;
            case 'sales': return <SalesView inventory={inventory} setInventory={setInventory} sales={sales} setSales={setSales} setAlertModal={setAlertModal} />;
            case 'import': return <ImportView inventory={inventory} setInventory={setInventory} setAlertModal={setAlertModal} />;
            default: return <DashboardView inventory={inventory} sales={sales} />;
        }
    };

    return (
        <div className="flex h-screen bg-slate-50 font-sans text-slate-800 overflow-hidden">
            <aside className={`${isSidebarOpen ? 'w-64' : 'w-20'} bg-slate-900 text-white transition-all duration-300 flex flex-col shadow-xl z-20`}>
                <div className="p-4 flex items-center justify-between border-b border-slate-700">
                    {isSidebarOpen && <h1 className="font-bold text-xl tracking-tight text-blue-400">Inventario<span className="text-white">Pro</span></h1>}
                    <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-1 rounded hover:bg-slate-700">
                        <Menu size={20} />
                    </button>
                </div>

                <nav className="flex-1 py-6 space-y-2 px-2">
                    <SidebarItem icon={<LayoutDashboard size={22} />} label="Tablero" active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')} expanded={isSidebarOpen} />
                    <SidebarItem icon={<Package size={22} />} label="Inventario" active={activeTab === 'inventory'} onClick={() => setActiveTab('inventory')} expanded={isSidebarOpen} />
                    <SidebarItem icon={<ShoppingCart size={22} />} label="Crear Orden Salida" active={activeTab === 'sales'} onClick={() => setActiveTab('sales')} expanded={isSidebarOpen} />
                    <SidebarItem icon={<FileText size={22} />} label="Importar PDF" active={activeTab === 'import'} onClick={() => setActiveTab('import')} expanded={isSidebarOpen} />
                </nav>

                <div className="p-4 border-t border-slate-800 text-xs text-slate-400 text-center">
                    {isSidebarOpen ? 'v1.4.0 - Price Markup' : 'v1.2'}
                </div>
            </aside>

            <main className="flex-1 overflow-auto relative">
                <header className="bg-white shadow-sm p-4 flex justify-between items-center sticky top-0 z-10">
                    <h2 className="text-xl font-semibold text-slate-700">
                        {activeTab === 'dashboard' && 'Resumen Ejecutivo'}
                        {activeTab === 'inventory' && 'Gestión de Productos Detallada'}
                        {activeTab === 'sales' && 'Gestión de Egresos de Mercancía'}
                        {activeTab === 'import' && 'Procesamiento de Documentos'}
                    </h2>
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold border border-blue-200">
                            AD
                        </div>
                        <div className="text-sm hidden sm:block text-right">
                            <p className="font-medium">Admin</p>
                            <p className="text-slate-400 text-xs">Gerente de Logística</p>
                        </div>
                    </div>
                </header>
                <div className="p-6">
                    {renderContent()}
                </div>
            </main>


            {/* Modals */}
            <Modal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                title={confirmModal.title}
                message={confirmModal.message}
                type="confirm"
                onConfirm={confirmModal.onConfirm}
                confirmText="Eliminar"
                cancelText="Cancelar"
            />

            <Modal
                isOpen={alertModal.isOpen}
                onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
                title={alertModal.title}
                message={alertModal.message}
                type={alertModal.type}
                confirmText="Entendido"
            />
        </div >
    );
}

const SidebarItem = ({ icon, label, active, onClick, expanded }: any) => (
    <button
        onClick={onClick}
        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors duration-200
      ${active ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`}
    >
        <div className="min-w-[24px] flex justify-center">{icon}</div>
        {expanded && <span className="font-medium whitespace-nowrap">{label}</span>}
    </button>
);

const DashboardView = ({ inventory, sales }: { inventory: Product[], sales: SaleOrder[] }) => {
    const totalStockValue = inventory.reduce((acc, p) => acc + (p.price * p.stock), 0);
    const lowStockCount = inventory.filter(p => p.stock < 5).length;
    const totalSales = sales.reduce((acc, order) => acc + order.total, 0);

    const categoryData = useMemo(() => {
        const cats: Record<string, number> = {};
        inventory.forEach(p => {
            cats[p.category] = (cats[p.category] || 0) + p.stock;
        });
        return Object.keys(cats).map(k => ({ name: k, value: cats[k] }));
    }, [inventory]);

    const priceVariationData = inventory.map(p => ({
        name: p.sku,
        actual: p.price,
        anterior: p.lastPrice,
        variacion: ((p.price - p.lastPrice) / p.lastPrice * 100).toFixed(1)
    })).slice(0, 10);

    // KPI Calculations
    const inflationData = inventory.filter(p => p.lastPrice > 0);
    const avgInflation = inflationData.length > 0
        ? (inflationData.reduce((acc, p) => acc + ((p.price - p.lastPrice) / p.lastPrice), 0) / inflationData.length) * 100
        : 0;

    const priceIncreases = inventory.filter(p => p.price > p.lastPrice).length;
    const priceDecreases = inventory.filter(p => p.price < p.lastPrice).length;

    const topMover = inventory.reduce((max, p) => {
        const increase = p.lastPrice > 0 ? ((p.price - p.lastPrice) / p.lastPrice) : 0;
        return increase > (max.increase || 0) ? { name: p.name, increase } : max;
    }, { name: 'N/A', increase: 0 });

    return (
        <div className="space-y-6 animate-fadeIn">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard title="Valor Inventario Base" value={`$${totalStockValue.toLocaleString()}`} icon={<DollarSign className="text-green-500" />} change="+2.5%" />
                <KPICard title="Total Egresos" value={`$${totalSales.toLocaleString()}`} icon={<TrendingUp className="text-blue-500" />} change={`+${sales.length} órdenes`} />
                <KPICard title="Stock Bajo" value={lowStockCount} icon={<AlertCircle className="text-red-500" />} isAlert={lowStockCount > 0} subtext="Productos requieren atención" />
                <KPICard title="Total Productos" value={inventory.length} icon={<Package className="text-purple-500" />} subtext="SKUs activos" />
            </div>

            {/* Price Variation KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                    title="Inflación Promedio"
                    value={`${avgInflation.toFixed(2)}%`}
                    icon={<Percent className="text-orange-500" />}
                    subtext="Variación global de precios"
                />
                <KPICard
                    title="Productos al Alza"
                    value={priceIncreases}
                    icon={<ArrowUp className="text-red-500" />}
                    subtext="Incrementaron precio"
                />
                <KPICard
                    title="Productos a la Baja"
                    value={priceDecreases}
                    icon={<ArrowDown className="text-green-500" />}
                    subtext="Disminuyeron precio"
                />
                <KPICard
                    title="Mayor Aumento"
                    value={(topMover.increase * 100).toFixed(1) + '%'}
                    icon={<TrendingUp className="text-red-600" />}
                    subtext={topMover.name}
                    isAlert={topMover.increase > 0.2}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                    <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                        <BarChart3 size={18} /> Stock por Categoría
                    </h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={categoryData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" fontSize={12} />
                                <YAxis fontSize={12} />
                                <Tooltip cursor={{ fill: '#f1f5f9' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                    <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                        <TrendingUp size={18} /> Variación de Precios (Top SKUs)
                    </h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={priceVariationData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" fontSize={12} />
                                <YAxis domain={['auto', 'auto']} fontSize={12} />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="anterior" stroke="#94a3b8" strokeDasharray="5 5" name="Precio Anterior" />
                                <Line type="monotone" dataKey="actual" stroke="#10b981" strokeWidth={2} name="Precio Actual" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

const KPICard = ({ title, value, icon, change, isAlert, subtext }: any) => (
    <div className={`bg-white p-5 rounded-xl border ${isAlert ? 'border-red-200 bg-red-50' : 'border-slate-200'} shadow-sm flex flex-col justify-between h-32`}>
        <div className="flex justify-between items-start">
            <div>
                <p className="text-slate-500 text-sm font-medium">{title}</p>
                <h3 className="text-2xl font-bold text-slate-800 mt-1">{value}</h3>
            </div>
            <div className={`p-2 rounded-lg bg-slate-50 ${isAlert ? 'bg-red-100' : ''}`}>{icon}</div>
        </div>
        <div className="mt-2">
            {change && <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded-full">{change}</span>}
            {subtext && <span className="text-xs text-slate-400">{subtext}</span>}
        </div>
    </div>
);

const InventoryView = ({ inventory, setInventory, setConfirmModal, setAlertModal }: any) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);

    const [formData, setFormData] = useState<Partial<Product>>({
        name: '', sku: '', category: '', stock: 0, price: 0, measure: 'Unidad', taxRate: 19, discount: 0
    });

    const filteredInv = inventory.filter((p: Product) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sku.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleDelete = (id: string) => {
        setConfirmModal({
            isOpen: true,
            title: 'Eliminar Producto',
            message: '¿Seguro que quieres eliminar este producto? Esta acción no se puede deshacer.',
            onConfirm: async () => {
                try {
                    const response = await fetch(`/api/products/${id}`, {
                        method: 'DELETE'
                    });

                    if (!response.ok) {
                        throw new Error('Error al eliminar el producto');
                    }

                    setInventory(inventory.filter((p: Product) => p.id !== id));
                    setAlertModal({
                        isOpen: true,
                        title: 'Producto Eliminado',
                        message: 'El producto ha sido eliminado correctamente.',
                        type: 'success'
                    });
                } catch (error) {
                    console.error('Error deleting product:', error);
                    setAlertModal({
                        isOpen: true,
                        title: 'Error',
                        message: 'No se pudo eliminar el producto. Inténtalo de nuevo.',
                        type: 'error'
                    });
                }
            }
        });
    };

    const openEdit = (product: Product) => {
        setFormData(product);
        setEditingId(product.id);
        setIsModalOpen(true);
    };

    const openNew = () => {
        setFormData({ name: '', sku: '', category: 'General', stock: 0, price: 0, measure: 'Unidad', taxRate: 19, discount: 0 });
        setEditingId(null);
        setIsModalOpen(true);
    };

    const handleSave = (e: React.FormEvent) => {
        e.preventDefault();
        if (editingId) {
            setInventory(inventory.map((p: Product) => {
                if (p.id === editingId) {
                    const newPrice = Number(formData.price);
                    const lastPrice = p.price !== newPrice ? p.price : p.lastPrice;
                    return { ...p, ...formData, price: newPrice, lastPrice, updatedAt: new Date().toISOString().split('T')[0] };
                }
                return p;
            }));
        } else {
            const newProduct: Product = {
                id: Math.random().toString(36).substr(2, 9),
                name: formData.name || 'Sin nombre',
                sku: formData.sku || 'GEN-000',
                category: formData.category || 'General',
                stock: Number(formData.stock) || 0,
                measure: formData.measure || 'Unidad',
                price: Number(formData.price) || 0,
                taxRate: Number(formData.taxRate) || 0,
                discount: Number(formData.discount) || 0,
                lastPrice: Number(formData.price) || 0,
                updatedAt: new Date().toISOString().split('T')[0]
            };
            setInventory([...inventory, newProduct]);
        }
        setIsModalOpen(false);
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-3 text-slate-400" size={18} />
                    <input
                        type="text"
                        placeholder="Buscar por descripción o código..."
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button
                    onClick={openNew}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-medium transition-colors"
                >
                    <Plus size={18} /> Nuevo Ítem
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 text-slate-700 border-b border-slate-200 uppercase text-xs font-bold tracking-wider">
                            <tr>
                                <th className="p-4 text-center w-10">#</th>
                                <th className="p-4">Código</th>
                                <th className="p-4 text-center">Cant</th>
                                <th className="p-4 text-center">Medida</th>
                                <th className="p-4 min-w-[200px]">Descripción</th>
                                <th className="p-4 text-right">Valor Unitario</th>
                                <th className="p-4 text-center">% Impuesto</th>
                                <th className="p-4 text-right">Impuesto($)</th>
                                <th className="p-4 text-right text-red-600">Descuento</th>
                                <th className="p-4 text-right font-bold bg-slate-100">Valor Total</th>
                                <th className="p-4 text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredInv.map((p: Product, index: number) => {
                                const taxValue = p.price * (p.taxRate / 100);
                                const finalUnitPrice = p.price + taxValue - p.discount;
                                const totalLineValue = finalUnitPrice * p.stock;

                                return (
                                    <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="p-4 text-center text-slate-400 font-mono">{index + 1}</td>
                                        <td className="p-4 font-mono text-slate-600 font-medium">{p.sku}</td>
                                        <td className={`p-4 text-center font-bold ${p.stock < 5 ? 'text-red-500' : 'text-slate-700'}`}>{p.stock}</td>
                                        <td className="p-4 text-center text-slate-500 text-xs">{p.measure}</td>
                                        <td className="p-4 font-medium text-slate-800">{p.name}</td>
                                        <td className="p-4 text-right text-slate-600">
                                            <div className="flex flex-col items-end">
                                                <span>${p.price.toLocaleString()}</span>
                                                {p.lastPrice !== undefined && p.lastPrice !== p.price && (
                                                    <div className={`text-xs flex items-center gap-1 ${p.price > p.lastPrice ? 'text-green-500' : 'text-red-500'}`}>
                                                        <span className="line-through text-slate-400">${p.lastPrice.toLocaleString()}</span>
                                                        {p.price > p.lastPrice ? (
                                                            <ArrowUp size={12} />
                                                        ) : (
                                                            <ArrowDown size={12} />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-4 text-center text-slate-500">{p.taxRate}%</td>
                                        <td className="p-4 text-right text-slate-500">${taxValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                                        <td className="p-4 text-right text-red-500">-${p.discount.toLocaleString()}</td>
                                        <td className="p-4 text-right font-bold text-slate-800 bg-slate-50 border-l border-slate-100">
                                            ${totalLineValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                        </td>
                                        <td className="p-4 flex justify-center gap-2">
                                            <button onClick={() => openEdit(p)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Editar"><Edit size={16} /></button>
                                            <button onClick={() => handleDelete(p.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Eliminar"><Trash2 size={16} /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredInv.length === 0 && (
                                <tr>
                                    <td colSpan={11} className="p-8 text-center text-slate-400">No se encontraron productos.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-6 animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-slate-800">{editingId ? 'Editar Producto' : 'Nuevo Producto'}</h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
                        </div>
                        <form onSubmit={handleSave} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Código (SKU)</label>
                                    <input required type="text" className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" value={formData.sku} onChange={e => setFormData({ ...formData, sku: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Categoría (Filtro)</label>
                                    <select className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}>
                                        <option>Tecnología</option>
                                        <option>Oficina</option>
                                        <option>Hogar</option>
                                        <option>Industrial</option>
                                        <option>General</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Descripción del Producto</label>
                                <input required type="text" className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                            </div>

                            <div className="grid grid-cols-3 gap-4 bg-slate-50 p-4 rounded-lg border border-slate-100">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Stock (Cant)</label>
                                    <input required type="number" min="0" className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" value={formData.stock} onChange={e => setFormData({ ...formData, stock: Number(e.target.value) })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Medida</label>
                                    <select className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" value={formData.measure} onChange={e => setFormData({ ...formData, measure: e.target.value })}>
                                        <option>Unidad</option>
                                        <option>Caja</option>
                                        <option>Paquete</option>
                                        <option>Kg</option>
                                        <option>Metro</option>
                                        <option>Litro</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Valor Unitario</label>
                                    <input required type="number" min="0" step="0.01" className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none font-bold text-slate-700" value={formData.price} onChange={e => setFormData({ ...formData, price: Number(e.target.value) })} />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4 bg-blue-50 p-4 rounded-lg border border-blue-100">
                                <div>
                                    <label className="block text-sm font-medium text-blue-900 mb-1">% Impuesto (IVA)</label>
                                    <div className="relative">
                                        <input required type="number" min="0" step="1" className="w-full border border-blue-200 p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none pr-8" value={formData.taxRate} onChange={e => setFormData({ ...formData, taxRate: Number(e.target.value) })} />
                                        <span className="absolute right-3 top-2 text-blue-400 font-bold">%</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-red-900 mb-1">Descuento ($)</label>
                                    <input required type="number" min="0" step="0.01" className="w-full border border-red-200 p-2 rounded focus:ring-2 focus:ring-red-500 outline-none text-red-600" value={formData.discount} onChange={e => setFormData({ ...formData, discount: Number(e.target.value) })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-500 mb-1">Total Calculado (Unit)</label>
                                    <div className="w-full p-2 bg-white border border-slate-200 rounded text-slate-500 font-mono">
                                        ${((Number(formData.price || 0) + (Number(formData.price || 0) * Number(formData.taxRate || 0) / 100)) - Number(formData.discount || 0)).toLocaleString()}
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 mt-4">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
                                <button type="submit" className="px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium shadow-sm flex items-center gap-2"><Save size={18} /> Guardar Registro</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const SalesView = ({ inventory, setInventory, sales, setSales, setAlertModal }: any) => {
    const [cart, setCart] = useState<SaleItem[]>([]);
    const [search, setSearch] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [selectingProduct, setSelectingProduct] = useState<Product | null>(null);
    const [quantityInput, setQuantityInput] = useState(1);
    const [markupModalOpen, setMarkupModalOpen] = useState(false);
    const [markupTargetId, setMarkupTargetId] = useState<string | 'all' | null>(null);
    const [markupPercentage, setMarkupPercentage] = useState<number>(0);

    const initiateAdd = (product: Product) => {
        setSelectingProduct(product);
        setQuantityInput(1);
    };

    const confirmAdd = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectingProduct) return;

        if (quantityInput <= 0) {
            setAlertModal({
                isOpen: true,
                title: 'Cantidad Inválida',
                message: 'La cantidad debe ser mayor a 0',
                type: 'error'
            });
            return;
        }
        if (quantityInput > selectingProduct.stock) {
            setAlertModal({
                isOpen: true,
                title: 'Stock Insuficiente',
                message: `No hay suficiente stock. Máximo: ${selectingProduct.stock}`,
                type: 'error'
            });
            return;
        }

        setCart(prev => {
            const existing = prev.find(item => item.productId === selectingProduct.id);
            if (existing) {
                const newQty = existing.quantity + quantityInput;
                if (newQty > selectingProduct.stock) {
                    setAlertModal({
                        isOpen: true,
                        title: 'Stock Insuficiente',
                        message: `No puedes agregar más. Total en orden: ${existing.quantity}. Stock máximo: ${selectingProduct.stock}`,
                        type: 'error'
                    });
                    return prev;
                }
                return prev.map(item => item.productId === selectingProduct.id ? { ...item, quantity: newQty } : item);
            }
            return [...prev, { productId: selectingProduct.id, quantity: quantityInput, priceAtSale: selectingProduct.price }];
        });

        setSelectingProduct(null);
        setQuantityInput(1);
    };

    const removeFromCart = (productId: string) => {
        setCart(prev => prev.filter(item => item.productId !== productId));
    };

    const openMarkupModal = (target: string | 'all') => {
        setMarkupTargetId(target);
        setMarkupPercentage(0);
        setMarkupModalOpen(true);
    };

    const applyMarkup = (e: React.FormEvent) => {
        e.preventDefault();
        if (markupPercentage === 0) return setMarkupModalOpen(false);

        setCart(prevCart => prevCart.map(item => {
            if (markupTargetId === 'all') {
                const product = inventory.find((p: Product) => p.id === item.productId);
                if (!product) return item;
                const newPrice = product.price * (1 + markupPercentage / 100);
                return { ...item, priceAtSale: newPrice };
            }
            else if (item.productId === markupTargetId) {
                const product = inventory.find((p: Product) => p.id === item.productId);
                if (!product) return item;
                const newPrice = product.price * (1 + markupPercentage / 100);
                return { ...item, priceAtSale: newPrice };
            }
            return item;
        }));

        setMarkupModalOpen(false);
    };

    const cartTotal = cart.reduce((acc, item) => {
        const p = inventory.find((x: Product) => x.id === item.productId);
        if (!p) return acc;
        const taxValue = item.priceAtSale * (p.taxRate / 100);
        const finalUnitPrice = item.priceAtSale + taxValue - p.discount;
        return acc + (finalUnitPrice * item.quantity);
    }, 0);

    const cartBaseTotal = cart.reduce((acc, item) => {
        const p = inventory.find((x: Product) => x.id === item.productId);
        if (!p) return acc;
        // Base price calculation (original price + tax - discount)
        const taxValue = p.price * (p.taxRate / 100);
        const finalBasePrice = p.price + taxValue - p.discount;
        return acc + (finalBasePrice * item.quantity);
    }, 0);

    const markupDiff = cartTotal - cartBaseTotal;
    const markupPercent = cartBaseTotal > 0 ? (markupDiff / cartBaseTotal) * 100 : 0;

    const handleCheckout = () => {
        if (cart.length === 0) return;

        const newOrder: SaleOrder = {
            id: `ORD-${Date.now().toString().slice(-6)}`,
            date: new Date().toISOString().split('T')[0],
            items: cart,
            total: cartTotal,
            status: 'completed'
        };

        const newInventory = inventory.map((p: Product) => {
            const cartItem = cart.find(c => c.productId === p.id);
            if (cartItem) {
                return { ...p, stock: p.stock - cartItem.quantity };
            }
            return p;
        });

        setInventory(newInventory);
        setSales([newOrder, ...sales]);
        setCart([]);
        setSuccessMsg("¡Orden generada correctamente!");
        setTimeout(() => setSuccessMsg(""), 3000);
    };

    const filteredProducts = inventory.filter((p: Product) =>
        (p.name.toLowerCase().includes(search.toLowerCase()) ||
            p.sku.toLowerCase().includes(search.toLowerCase())) && p.stock > 0
    );

    return (
        <div className="flex flex-col gap-6 h-[calc(100vh-140px)]">
            <div className="flex-1 flex flex-col gap-4 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden min-h-0">
                <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                    <h3 className="font-bold text-slate-700 mr-4">Catálogo de Productos</h3>
                    <div className="relative flex-1 max-w-md flex items-center">
                        <Search size={16} className="absolute left-3 text-slate-400" />
                        <input
                            className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-4 py-2 outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            placeholder="Buscar por Nombre o Código..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && <button onClick={() => setSearch('')} className="absolute right-3 text-slate-400 hover:text-slate-600"><X size={14} /></button>}
                    </div>
                </div>

                <div className="flex-1 overflow-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 text-slate-700 border-b border-slate-200 uppercase text-xs font-bold tracking-wider sticky top-0 z-10">
                            <tr>
                                <th className="p-3 text-center w-10">#</th>
                                <th className="p-3">Código</th>
                                <th className="p-3 text-center">Cant</th>
                                <th className="p-3 text-center">Medida</th>
                                <th className="p-3 min-w-[200px]">Descripción</th>
                                <th className="p-3 text-right">Valor Unitario</th>
                                <th className="p-3 text-center">% Impuesto</th>
                                <th className="p-3 text-right">Impuesto($)</th>
                                <th className="p-3 text-right text-red-600">Descuento</th>
                                <th className="p-3 text-right font-bold bg-slate-100">Valor Total</th>
                                <th className="p-3 text-center">Acción</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredProducts.map((p: Product, index: number) => {
                                const taxValue = p.price * (p.taxRate / 100);
                                const finalUnitPrice = p.price + taxValue - p.discount;
                                const totalLineValue = finalUnitPrice * p.stock;

                                return (
                                    <tr key={p.id} className="hover:bg-blue-50 transition-colors group">
                                        <td className="p-3 text-center text-slate-400 font-mono">{index + 1}</td>
                                        <td className="p-3 font-mono text-slate-500">{p.sku}</td>
                                        <td className={`p-3 text-center font-bold ${p.stock < 5 ? 'text-red-500' : 'text-slate-700'}`}>{p.stock}</td>
                                        <td className="p-3 text-center text-slate-500 text-xs">{p.measure}</td>
                                        <td className="p-3 font-medium text-slate-800">{p.name}</td>
                                        <td className="p-3 text-right text-slate-600">${p.price.toLocaleString()}</td>
                                        <td className="p-3 text-center text-slate-500">{p.taxRate}%</td>
                                        <td className="p-3 text-right text-slate-500">${taxValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                                        <td className="p-3 text-right text-red-500">-${p.discount.toLocaleString()}</td>
                                        <td className="p-3 text-right font-bold text-slate-800 bg-slate-50 border-l border-slate-100">
                                            ${totalLineValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                        </td>
                                        <td className="p-3 text-center">
                                            <button
                                                onClick={() => initiateAdd(p)}
                                                className="px-3 py-1 bg-white border border-slate-200 text-slate-600 rounded hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all text-xs font-medium shadow-sm"
                                            >
                                                Agregar
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredProducts.length === 0 && (
                                <tr>
                                    <td colSpan={11} className="p-8 text-center text-slate-400">
                                        No se encontraron productos disponibles.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex-1 bg-white rounded-xl shadow-xl border border-slate-200 flex flex-col min-h-0 z-10">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 rounded-t-xl">
                    <h3 className="font-bold text-slate-700 flex items-center gap-2"><ShoppingCart size={20} /> Orden de Salida Actual</h3>
                    <div className="flex gap-2">
                        <button
                            onClick={() => openMarkupModal('all')}
                            className="px-3 py-1.5 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 rounded-lg text-xs font-bold transition-colors flex items-center gap-2 border border-yellow-200"
                        >
                            <Percent size={14} /> Incremento Global
                        </button>
                        <span className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-bold">{cart.length} items</span>
                    </div>
                </div>

                <div className="flex-1 overflow-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 text-slate-700 border-b border-slate-200 uppercase text-xs font-bold tracking-wider sticky top-0 z-10">
                            <tr>
                                <th className="p-3 text-center w-10">#</th>
                                <th className="p-3">Código</th>
                                <th className="p-3 text-center">Cant</th>
                                <th className="p-3 text-center">Medida</th>
                                <th className="p-3 min-w-[200px]">Descripción</th>
                                <th className="p-3 text-right">Valor Unitario</th>
                                <th className="p-3 text-center">% Impuesto</th>
                                <th className="p-3 text-right">Impuesto($)</th>
                                <th className="p-3 text-right text-red-600">Descuento</th>
                                <th className="p-3 text-right font-bold bg-slate-100">Valor Total</th>
                                <th className="p-3 text-center">Acción</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {cart.map((item, index) => {
                                const p = inventory.find((x: Product) => x.id === item.productId);
                                if (!p) return null;

                                const taxValue = item.priceAtSale * (p.taxRate / 100);
                                const finalUnitPrice = item.priceAtSale + taxValue - p.discount;
                                const totalLineValue = finalUnitPrice * item.quantity;

                                return (
                                    <tr key={item.productId} className="hover:bg-blue-50 transition-colors group">
                                        <td className="p-3 text-center text-slate-400 font-mono">{index + 1}</td>
                                        <td className="p-3 font-mono text-slate-500">{p.sku}</td>
                                        <td className="p-3 text-center font-bold text-blue-600">{item.quantity}</td>
                                        <td className="p-3 text-center text-slate-500 text-xs">{p.measure}</td>
                                        <td className="p-3 font-medium text-slate-800">{p.name}</td>
                                        <td className="p-3 text-right text-slate-600">
                                            <div className="flex flex-col items-end">
                                                <span>${item.priceAtSale.toLocaleString()}</span>
                                                {item.priceAtSale !== p.price && (
                                                    <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1 rounded">Modif.</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-3 text-center text-slate-500">{p.taxRate}%</td>
                                        <td className="p-3 text-right text-slate-500">${(taxValue * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                                        <td className="p-3 text-right text-red-500">-${(p.discount * item.quantity).toLocaleString()}</td>
                                        <td className="p-3 text-right font-bold text-slate-800 bg-slate-50 border-l border-slate-100">
                                            ${totalLineValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                        </td>
                                        <td className="p-3 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => openMarkupModal(item.productId)}
                                                    title="Incrementar precio %"
                                                    className="text-yellow-600 bg-yellow-50 hover:bg-yellow-100 p-1.5 rounded transition-colors"
                                                >
                                                    <Percent size={14} />
                                                </button>
                                                <button
                                                    onClick={() => removeFromCart(item.productId)}
                                                    className="text-red-400 bg-red-50 hover:bg-red-100 p-1.5 rounded transition-colors"
                                                    title="Eliminar"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {cart.length === 0 && (
                                <tr>
                                    <td colSpan={11} className="p-8 text-center text-slate-400">
                                        <div className="flex flex-col items-center justify-center">
                                            <Package size={48} className="mb-2 opacity-20" />
                                            <p className="text-sm">Selecciona productos arriba para armar la orden.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="p-4 bg-slate-50 border-t border-slate-200 rounded-b-xl flex justify-between items-center">
                    <div className="flex gap-8">
                        <div className="flex flex-col">
                            <span className="text-xs text-slate-500 uppercase font-bold">Total a Pagar / Egreso</span>
                            <span className="font-bold text-slate-800 text-2xl">${cartTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                        </div>
                        {markupDiff !== 0 && (
                            <>
                                <div className="flex flex-col">
                                    <span className="text-xs text-slate-500 uppercase font-bold">Incremento Total ($)</span>
                                    <span className={`font-bold text-lg ${markupDiff > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {markupDiff > 0 ? '+' : ''}{markupDiff.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                    </span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs text-slate-500 uppercase font-bold">Incremento Global (%)</span>
                                    <span className={`font-bold text-lg ${markupPercent > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {markupPercent > 0 ? '+' : ''}{markupPercent.toFixed(1)}%
                                    </span>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        {successMsg && (
                            <span className="text-green-600 text-sm font-medium animate-in fade-in flex items-center gap-1">
                                <CheckCircle size={14} /> {successMsg}
                            </span>
                        )}

                        {cart.length > 0 && (
                            <button
                                onClick={() => openMarkupModal('all')}
                                className="px-4 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl font-bold text-sm shadow-md transition-all flex items-center gap-2"
                            >
                                <Percent size={18} /> Incr. Global
                            </button>
                        )}

                        <button
                            onClick={handleCheckout}
                            disabled={cart.length === 0}
                            className={`px-8 py-3 rounded-xl font-bold text-lg shadow-lg flex items-center justify-center gap-2 transition-all
                ${cart.length > 0 ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-blue-500/30' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}
                        >
                            Confirmar Salida <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>

            {selectingProduct && (
                <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 animate-in fade-in zoom-in duration-200">
                        <h3 className="text-lg font-bold text-slate-800 mb-1">Agregar a la Orden</h3>
                        <p className="text-sm text-slate-500 mb-4">{selectingProduct.name}</p>

                        <form onSubmit={confirmAdd}>
                            <div className="mb-4">
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Cantidad a Despachar</label>
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setQuantityInput(Math.max(1, quantityInput - 1))}
                                        className="w-10 h-10 rounded-lg border border-slate-300 flex items-center justify-center hover:bg-slate-50"
                                    >
                                        -
                                    </button>
                                    <input
                                        type="number"
                                        min="1"
                                        max={selectingProduct.stock}
                                        className="flex-1 h-10 text-center font-bold text-lg border border-slate-300 rounded-lg outline-none focus:border-blue-500"
                                        value={quantityInput}
                                        onChange={(e) => setQuantityInput(Number(e.target.value))}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setQuantityInput(Math.min(selectingProduct.stock, quantityInput + 1))}
                                        className="w-10 h-10 rounded-lg border border-slate-300 flex items-center justify-center hover:bg-slate-50"
                                    >
                                        +
                                    </button>
                                </div>
                                <div className="text-right mt-1 text-xs text-slate-400">
                                    Disponible: {selectingProduct.stock}
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setSelectingProduct(null)}
                                    className="flex-1 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-md"
                                >
                                    Confirmar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {markupModalOpen && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 animate-in fade-in zoom-in duration-200">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-yellow-100 rounded-lg text-yellow-600"><Percent size={24} /></div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-800">
                                    {markupTargetId === 'all' ? 'Incremento Global' : 'Incremento Individual'}
                                </h3>
                                <p className="text-xs text-slate-500">
                                    {markupTargetId === 'all' ? 'Aplica a todos los items de la orden' : 'Aplica solo a este item'}
                                </p>
                            </div>
                        </div>

                        <form onSubmit={applyMarkup}>
                            <div className="mb-6">
                                <label className="block text-sm font-bold text-slate-700 mb-2">Porcentaje de Aumento (%)</label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        min="0"
                                        max="1000"
                                        step="0.1"
                                        autoFocus
                                        className="w-full border-2 border-slate-200 p-3 rounded-xl text-2xl font-bold text-center outline-none focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100 transition-all"
                                        value={markupPercentage}
                                        onChange={(e) => setMarkupPercentage(Number(e.target.value))}
                                    />
                                    <span className="absolute right-4 top-4 text-slate-400 font-bold">%</span>
                                </div>
                                <p className="text-xs text-slate-400 mt-2 text-center">El precio base se multiplicará por (1 + %)</p>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setMarkupModalOpen(false)}
                                    className="flex-1 py-3 text-slate-600 hover:bg-slate-100 rounded-xl font-medium"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-3 bg-yellow-500 text-white rounded-xl font-bold hover:bg-yellow-600 shadow-lg shadow-yellow-500/30"
                                >
                                    Aplicar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const ImportView = ({ inventory, setInventory, setAlertModal }: any) => {
    const [isDragging, setIsDragging] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [processing, setProcessing] = useState(false);
    const [previewData, setPreviewData] = useState<Product[] | null>(null);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setFile(e.dataTransfer.files[0]);
        }
    };

    const processFile = async () => {
        if (!file) return;
        setProcessing(true);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch('/api/import/pdf', {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Error procesando el PDF');
            }

            if (data.products && data.products.length > 0) {
                // Convert backend format to frontend format
                const products = data.products.map((p: any) => ({
                    id: `temp-${Date.now()}-${Math.random()}`,
                    name: p.name,
                    sku: p.sku,
                    category: p.category,
                    stock: p.stock,
                    measure: p.measure || 'Unidad',
                    price: p.price,
                    currentPrice: p.currentPrice, // Existing price from DB
                    currentStock: p.currentStock, // Existing stock from DB
                    taxRate: p.taxRate !== undefined ? p.taxRate : 0,
                    discount: p.discount || 0,
                    lastPrice: p.price,
                    updatedAt: new Date().toISOString().split('T')[0]
                }));

                setPreviewData(products);
            } else {
                setAlertModal({
                    isOpen: true,
                    title: 'Error de Extracción',
                    message: 'No se pudieron extraer productos del archivo. Verifica que el formato sea correcto.',
                    type: 'error'
                });
                setFile(null);
            }
        } catch (error: any) {
            console.error('Error processing PDF:', error);
            setAlertModal({
                isOpen: true,
                title: 'Error Procesando Archivo',
                message: `Error procesando el archivo: ${error.message}`,
                type: 'error'
            });
            setFile(null);
        } finally {
            setProcessing(false);
        }
    };

    const confirmImport = async () => {
        if (!previewData) return;

        try {
            // Send products to backend for import
            const response = await fetch('/api/import/products', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    products: previewData.map(p => ({
                        name: p.name,
                        sku: p.sku,
                        category: p.category,
                        stock: p.stock,
                        measure: p.measure,
                        price: p.price,
                        taxRate: p.taxRate,
                        discount: p.discount
                    }))
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Error importando productos');
            }

            // Reload inventory from database
            const inventoryResponse = await fetch('/api/products');
            const updatedInventory = await inventoryResponse.json();

            // Convert backend format to frontend format
            const formattedInventory = updatedInventory.map((p: any) => ({
                id: p.id.toString(),
                name: p.name,
                sku: p.sku,
                category: p.category,
                stock: p.stock,
                measure: p.measure,
                price: parseFloat(p.price),
                taxRate: p.tax_rate,
                discount: parseFloat(p.discount),
                lastPrice: parseFloat(p.last_price),
                updatedAt: p.updated_at.split('T')[0]
            }));

            setInventory(formattedInventory);
            setFile(null);
            setPreviewData(null);

            setAlertModal({
                isOpen: true,
                title: 'Importación Exitosa',
                message: `¡Se han importado ${data.imported} productos al inventario correctamente!`,
                type: 'success'
            });
        } catch (error: any) {
            console.error('Error importing products:', error);
            setAlertModal({
                isOpen: true,
                title: 'Error de Importación',
                message: `No se pudieron importar los productos: ${error.message}`,
                type: 'error'
            });
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8">
            <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-slate-800">Carga de Documentos</h2>
                <p className="text-slate-500">Sube tus facturas o listas de inventario (PDF, Excel) para actualizar el stock automáticamente.</p>
            </div>

            {!previewData ? (
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    className={`
            border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer
            ${isDragging ? 'border-blue-500 bg-blue-50 scale-[1.02]' : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'}
          `}
                >
                    <input
                        type="file"
                        className="hidden"
                        id="fileInput"
                        accept=".pdf,.xml,.csv,.xlsx"
                        onChange={(e) => e.target.files && setFile(e.target.files[0])}
                    />
                    <label htmlFor="fileInput" className="cursor-pointer flex flex-col items-center">
                        <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4">
                            {processing ? <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div> : <UploadCloud size={32} />}
                        </div>
                        {file ? (
                            <div className="space-y-4">
                                <p className="font-bold text-lg text-slate-800">{file.name}</p>
                                <p className="text-sm text-slate-500">{(file.size / 1024).toFixed(2)} KB</p>
                                {!processing && (
                                    <button onClick={(e) => { e.preventDefault(); processFile(); }} className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 shadow-lg shadow-blue-500/30">
                                        Procesar Documento
                                    </button>
                                )}
                                {processing && <p className="text-blue-600 font-medium animate-pulse">Analizando documento con IA...</p>}
                            </div>
                        ) : (
                            <>
                                <p className="text-lg font-medium text-slate-700">Arrastra tu archivo aquí o <span className="text-blue-600">haz clic para buscar</span></p>
                                <p className="text-sm text-slate-400 mt-2">Soporta PDF, XML (Factura Electrónica), Facturas escaneadas</p>
                            </>
                        )}
                    </label>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden animate-in slide-in-from-bottom-4">
                    <div className="p-4 bg-green-50 border-b border-green-100 flex justify-between items-center">
                        <div className="flex items-center gap-2 text-green-800 font-bold">
                            <CheckCircle size={20} />
                            Se detectaron {previewData.length} productos nuevos
                        </div>
                        <button onClick={() => setPreviewData(null)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
                    </div>
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-slate-600">
                            <tr>
                                <th className="p-4">SKU Detectado</th>
                                <th className="p-4">Producto</th>
                                <th className="p-4 text-center">Cantidad</th>
                                <th className="p-4 text-right">Costo Unit.</th>
                                <th className="p-4 text-right">Subtotal</th>
                                <th className="p-4 text-center">% Imp.</th>
                                <th className="p-4 text-right">Impuesto ($)</th>
                                <th className="p-4 text-right">Descuento</th>
                                <th className="p-4 text-right font-bold">Total</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {previewData.map((p, idx) => {
                                const subtotal = p.price * p.stock;
                                const taxAmount = subtotal * (p.taxRate / 100);
                                const total = subtotal + taxAmount - p.discount;

                                return (
                                    <tr key={idx}>
                                        <td className="p-4 font-mono text-slate-500">{p.sku}</td>
                                        <td className="p-4 font-medium">{p.name}</td>
                                        <td className="p-4 text-center font-bold text-blue-600">+{p.stock}</td>
                                        <td className="p-4 text-right">
                                            <div className="flex flex-col items-end">
                                                <span className="font-bold">${p.price.toLocaleString()}</span>
                                                {p.currentPrice !== undefined && p.currentPrice !== p.price && (
                                                    <div className={`text-xs flex items-center gap-1 ${p.price > p.currentPrice ? 'text-red-500' : 'text-green-500'}`}>
                                                        <span className="line-through text-slate-400">${p.currentPrice.toLocaleString()}</span>
                                                        {p.price > p.currentPrice ? (
                                                            <ArrowUp size={12} />
                                                        ) : (
                                                            <ArrowDown size={12} />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-4 text-right text-slate-600">${subtotal.toLocaleString()}</td>
                                        <td className="p-4 text-center">{p.taxRate}%</td>
                                        <td className="p-4 text-right text-slate-500">${taxAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                        <td className="p-4 text-right text-red-500">{p.discount > 0 ? `-$${p.discount.toLocaleString()}` : '$0'}</td>
                                        <td className="p-4 text-right font-bold text-slate-800 bg-slate-50">${total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="bg-slate-50 border-t border-slate-200 p-4">
                        <div className="flex justify-end gap-8 text-sm">
                            <div className="text-right">
                                <span className="block text-slate-500 text-xs uppercase font-bold">Total Unidades</span>
                                <span className="font-bold text-slate-800 text-lg">
                                    {previewData.reduce((acc, p) => acc + p.stock, 0)}
                                </span>
                            </div>
                            <div className="text-right">
                                <span className="block text-slate-500 text-xs uppercase font-bold">Subtotal</span>
                                <span className="font-medium text-slate-800 text-lg">
                                    ${previewData.reduce((acc, p) => acc + (p.price * p.stock), 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </span>
                            </div>
                            <div className="text-right">
                                <span className="block text-slate-500 text-xs uppercase font-bold">Total Impuestos</span>
                                <span className="font-medium text-slate-800 text-lg">
                                    ${previewData.reduce((acc, p) => acc + ((p.price * p.stock) * (p.taxRate / 100)), 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </span>
                            </div>
                            <div className="text-right">
                                <span className="block text-slate-500 text-xs uppercase font-bold">Total a Pagar</span>
                                <span className="font-bold text-green-600 text-xl">
                                    ${previewData.reduce((acc, p) => {
                                        const sub = p.price * p.stock;
                                        const tax = sub * (p.taxRate / 100);
                                        return acc + (sub + tax - p.discount);
                                    }, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="p-4 bg-white border-t border-slate-200 flex justify-end gap-3">
                        <button onClick={() => setPreviewData(null)} className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg">Descartar</button>
                        <button onClick={confirmImport} className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 shadow-md">Confirmar e Importar</button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                <div className="p-4 border border-slate-200 rounded-lg bg-white">
                    <div className="w-8 h-8 bg-orange-100 text-orange-600 rounded flex items-center justify-center mb-2"><FileText size={16} /></div>
                    <h4 className="font-bold text-sm">OCR Inteligente</h4>
                    <p className="text-xs text-slate-500 mt-1">Detecta tablas en facturas PDF automáticamente.</p>
                </div>
                <div className="p-4 border border-slate-200 rounded-lg bg-white">
                    <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded flex items-center justify-center mb-2"><TrendingUp size={16} /></div>
                    <h4 className="font-bold text-sm">Historial de Precios</h4>
                    <p className="text-xs text-slate-500 mt-1">Si el precio cambia en el PDF, se registra la variación.</p>
                </div>
                <div className="p-4 border border-slate-200 rounded-lg bg-white">
                    <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded flex items-center justify-center mb-2"><Save size={16} /></div>
                    <h4 className="font-bold text-sm">Auto-Guardado</h4>
                    <p className="text-xs text-slate-500 mt-1">Los datos se sincronizan con tu inventario principal.</p>
                </div>
            </div>
        </div>
    );
};
